<template>
  <div class="modal fade" id="companyContractsModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Contratos - {{ company?.company_name }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Info da empresa -->
          <div class="d-flex align-items-center bg-light-primary rounded p-3 mb-4" v-if="company">
            <div class="me-4">
              <i class="bi bi-building fs-2 text-primary"></i>
            </div>
            <div class="flex-grow-1">
              <div class="fw-bold fs-5 text-dark">{{ company.company_name }}</div>
              <div class="d-flex gap-4 mt-1">
                <span class="text-gray-700 fs-6"><strong>CNPJ:</strong> {{ company.formatted_cnpj }}</span>
              </div>
            </div>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-primary" @click="openNewContractModal">
                <i class="bi bi-plus-lg me-1"></i> Novo contrato
              </button>
            </div>
          </div>

          <!-- Todos os Contratos -->
          <div>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="text-muted mb-0 fw-bold">Todos os Contratos</h6>
              <div class="btn-group btn-group-sm" role="group">
                <button type="button" class="btn"
                        :class="viewMode === 'card' ? 'btn-primary' : 'btn-light'"
                        @click="viewMode = 'card'" title="Visualizar em cards">
                  <i class="bi bi-grid-3x3-gap"></i>
                </button>
                <button type="button" class="btn"
                        :class="viewMode === 'table' ? 'btn-primary' : 'btn-light'"
                        @click="viewMode = 'table'" title="Visualizar em tabela">
                  <i class="bi bi-list-ul"></i>
                </button>
              </div>
            </div>
            <div v-if="contracts.length === 0" class="text-center text-muted py-5">
              Nenhum contrato.
            </div>
            <div v-else-if="viewMode === 'card'" class="row g-3">
              <div v-for="contract in contracts" :key="contract.id_contract" class="col-12 col-md-6 col-xl-4">
                <div class="card card-bordered h-100">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <span class="fw-bold text-dark">{{ contract.contract_number || '#' + contract.id_contract }}</span>
                        <span :class="getStatusBadgeClass(contract)" class="ms-2">
                          {{ getStatusDisplayText(contract) }}
                        </span>
                      </div>
                    </div>
                    <p class="text-muted small mb-2">{{ contract.plan?.plan_name || '-' }}</p>
                    <div class="d-flex flex-wrap gap-3 mb-2 fs-7">
                      <span><strong>Início:</strong> {{ formatDate(contract.start_date) }}</span>
                      <span><strong>Válido até:</strong> {{ formatDate(contract.valid_until) }}</span>
                    </div>
                    <div class="d-flex flex-wrap gap-3 mb-3 fs-7">
                      <span><strong>Titular:</strong> {{ formatMoney(contract.subs_base_price) }}</span>
                      <span><strong>Dependente:</strong> {{ formatMoney(contract.subs_dependent_price) }}</span>
                    </div>
                    <div v-if="contract.contract_status === 'cancelled'" class="fs-7 text-muted mb-3">
                      <div><strong>Cancelado por:</strong> {{ contract.cancelled_by?.user_name || '-' }}</div>
                      <div><strong>Data:</strong> {{ formatDateTime(contract.cancelled_at) }}</div>
                      <div v-if="contract.cancellation_reason" class="mt-1">
                        <strong>Motivo:</strong> {{ contract.cancellation_reason }}
                      </div>
                    </div>
                    <div class="d-flex flex-wrap gap-1">
                      <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                              type="button" @click="openEditContractModal(contract)" title="Editar contrato">
                        <i class="bi bi-pen"></i>
                      </button>
                      <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                              type="button" @click="downloadContract(contract.id_contract)" title="Baixar contrato">
                        <i class="bi bi-download"></i>
                      </button>
                      <button v-if="canRenewContract(contract)"
                              class="btn btn-sm btn-icon btn-success"
                              type="button" @click="openRenewContractModal(contract)" title="Renovar contrato">
                        <i class="bi bi-arrow-repeat"></i>
                      </button>
                      <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                              type="button" @click="openRenewHistoryModal(contract)" title="Histórico de renovações">
                        <i class="bi bi-clock-history"></i>
                      </button>
                      <button v-if="contract.contract_status === 'active'"
                              class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger border border-gray-400"
                              type="button" @click="openCancelContractModal(contract)" title="Cancelar contrato">
                        <i class="bi bi-x-circle"></i>
                      </button>
                      <button v-if="contract.contract_status === 'cancelled'"
                              class="btn btn-sm btn-icon btn-bg-light btn-active-color-success border border-gray-400"
                              type="button" @click="confirmReopenContract(contract)" title="Reabrir contrato">
                        <i class="bi bi-arrow-counterclockwise"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover table-row-bordered">
                <thead>
                <tr class="fw-bold fs-6 text-muted">
                  <th>Número</th>
                  <th>Plano</th>
                  <th>Status</th>
                  <th>Início</th>
                  <th>Válido até</th>
                  <th>Titular</th>
                  <th>Dependente</th>
                  <th class="text-center">Ações</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="contract in contracts" :key="contract.id_contract">
                <tr>
                  <td>{{ contract.contract_number || '-' }}</td>
                  <td>{{ contract.plan?.plan_name || '-' }}</td>
                  <td>
                    <span :class="getStatusBadgeClass(contract)">
                      {{ getStatusDisplayText(contract) }}
                    </span>
                  </td>
                  <td>{{ formatDate(contract.start_date) }}</td>
                  <td>{{ formatDate(contract.valid_until) }}</td>
                  <td>{{ formatMoney(contract.subs_base_price) }}</td>
                  <td>{{ formatMoney(contract.subs_dependent_price) }}</td>
                  <td class="text-center">
                    <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400 me-1"
                            type="button" @click="openEditContractModal(contract)" title="Editar contrato">
                      <i class="bi bi-pen"></i>
                    </button>
                    <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400 me-1"
                            type="button" @click="downloadContract(contract.id_contract)" title="Baixar contrato">
                      <i class="bi bi-download"></i>
                    </button>
                    <button v-if="canRenewContract(contract)"
                            class="btn btn-sm btn-icon btn-success me-1"
                            type="button" @click="openRenewContractModal(contract)" title="Renovar contrato">
                      <i class="bi bi-arrow-repeat"></i>
                    </button>
                    <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400 me-1"
                            type="button" @click="openRenewHistoryModal(contract)" title="Histórico de renovações">
                      <i class="bi bi-clock-history"></i>
                    </button>
                    <button v-if="contract.contract_status === 'active'"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger border border-gray-400 me-1"
                            type="button" @click="openCancelContractModal(contract)" title="Cancelar contrato">
                      <i class="bi bi-x-circle"></i>
                    </button>
                    <button v-if="contract.contract_status === 'cancelled'"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-success border border-gray-400"
                            type="button" @click="confirmReopenContract(contract)" title="Reabrir contrato">
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="contract.contract_status === 'cancelled'">
                  <td colspan="8" class="bg-light">
                    <div class="p-3 small text-muted">
                      <div class="row">
                        <div class="col-md-6">
                          <strong>Cancelado por:</strong> {{ contract.cancelled_by?.user_name || '-' }}
                        </div>
                        <div class="col-md-6">
                          <strong>Data do cancelamento:</strong> {{ formatDateTime(contract.cancelled_at) }}
                        </div>
                      </div>
                      <div class="row mt-2" v-if="contract.cancellation_reason">
                        <div class="col-12">
                          <strong>Motivo:</strong>
                          <div class="mt-1 p-2 bg-white rounded border">{{ contract.cancellation_reason }}</div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Fechar</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Novo Contrato: plano + vigência -->
  <div class="modal fade" id="companyContractNewModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Novo Contrato</h5>
          <button type="button" class="btn-close" @click="closeNewContractModal"></button>
        </div>
        <div class="modal-body" v-if="company">
          <div class="d-flex align-items-center bg-light-primary rounded p-3 mb-3">
            <div class="me-3">
              <i class="bi bi-building fs-2 text-primary"></i>
            </div>
            <div class="flex-grow-1">
              <span class="fw-bold text-dark">{{ company.company_name }}</span>
              <span class="text-muted ms-2">{{ company.formatted_cnpj }}</span>
            </div>
          </div>
          <form @submit.prevent="submitNewContract">
            <h6 class="text-muted mb-2 fw-bold">DADOS DO CONTRATO</h6>
            <div class="row mb-3">
              <label class="form-label required">Plano</label>
              <select v-model="newContractForm.id_plan" class="form-control form-control-solid" required>
                <option :value="null">Selecione um plano</option>
                <option v-for="plan in corporatePlans" :key="plan.id_plan" :value="plan.id_plan">
                  {{ plan.plan_name }}
                </option>
              </select>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label required">Data início</label>
                <Calendar class="form-control form-control-sm w-100  fs-5"
                          dateFormat="dd/mm/yy"
                          v-model="newContractForm.start_date"
                          showButtonBar />
              </div>
              <div class="col-md-6">
                <label class="form-label required">Válido até</label>
                <Calendar class="form-control form-control-sm w-100 fs-5"
                          dateFormat="dd/mm/yy"
                          v-model="newContractForm.valid_until"
                          :minDate="minValidUntilDate"
                          showButtonBar />
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label required">Valor por titular</label>
                <money3 class="form-control form-control-solid"
                       v-model="newContractForm.subs_base_price"
                       v-bind="moneyConfig" />
              </div>
              <div class="col-md-6">
                <label class="form-label required">Valor por dependente</label>
                <money3 class="form-control form-control-solid"
                       v-model="newContractForm.subs_dependent_price"
                       v-bind="moneyConfig" />
              </div>
            </div>
            <div class="modal-footer px-0 pb-0">
              <button type="button" class="btn btn-secondary" @click="closeNewContractModal">Cancelar</button>
              <button type="submit" class="btn btn-primary">Gerar contrato</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Editar Contrato (estrutura baseada em CompanySubscriptionModal) -->
  <div class="modal fade" id="companyContractEditModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Editar Contrato - {{ contractToEdit?.contract_number || 'Contrato' }}</h5>
          <button type="button" class="btn-close" @click="closeEditContractModal"></button>
        </div>
        <div class="modal-body" v-if="contractToEdit">
          <div class="d-flex align-items-center bg-light-primary rounded p-3 mb-3" v-if="company">
            <div class="me-4">
              <i class="bi bi-building fs-2 text-primary"></i>
            </div>
            <div class="flex-grow-1">
              <span class="fw-bold fs-5 text-dark">{{ company.company_name }}</span>
              <span class="text-muted ms-3">{{ company.formatted_cnpj }}</span>
            </div>
          </div>
          <form @submit.prevent="submitEditContract">
            <div class="row mb-2">
              <div class="col-md-12">
                <h6 class="text-muted mb-2 fw-bold">DADOS DO CONTRATO</h6>
              </div>
              <div class="col-md-6">
                <label class="form-label">Número</label>
                <input type="text" class="form-control form-control-solid" disabled
                       :value="contractToEdit.contract_number || '-'" />
              </div>
              <div class="col-md-6 mt-8">
                <label class="form-label">Status</label>
                <span :class="getStatusBadgeClass(contractToEdit)" class="d-inline-block mt-2">
                  {{ getStatusDisplayText(contractToEdit) }}
                </span>
              </div>
              <div class="col-md-12">
                <label class="form-label">Plano</label>
                <input type="text" class="form-control form-control-solid" disabled
                       :value="contractToEdit.plan?.plan_name || '-'" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Modelo</label>
                <input type="text" class="form-control form-control-solid" disabled
                       :value="contractToEdit.template?.template_name || '-'" />
              </div>
              <div class="col-md-6 mt-2">
                <label class="form-label">Gerado em</label>
                <input type="text" class="form-control form-control-solid" disabled
                       :value="formatDate(contractToEdit.created_at)" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-md-6">
                <label class="form-label">Data início</label>
                <Calendar class="form-control form-control-solid form-control-sm w-100 fs-5"
                          dateFormat="dd/mm/yy"
                          v-model="editContractForm.start_date"
                          disabled
                          showButtonBar />
              </div>
              <div class="col-md-6">
                <label class="form-label">Válido até</label>
                <Calendar class="form-control form-control-solid form-control-sm w-100 fs-5"
                          dateFormat="dd/mm/yy"
                          v-model="editContractForm.valid_until"
                          disabled
                          showButtonBar />
              </div>
              <div class="col-md-6">
                <label class="form-label">Valor por titular</label>
                <money3 class="form-control form-control-solid"
                       v-model="editContractForm.subs_base_price"
                       v-bind="moneyConfig"
                        disabled />
              </div>
              <div class="col-md-6 mt-2">
                <label class="form-label">Valor por dependente</label>
                <money3 class="form-control form-control-solid"
                       v-model="editContractForm.subs_dependent_price"
                       v-bind="moneyConfig"
                       disabled />
              </div>
            </div>
            <div class="d-flex gap-2 mt-3">
              <button type="button" class="btn btn-primary btn-sm" @click="downloadContract(contractToEdit.id_contract); closeEditContractModal();">
                <i class="bi bi-download me-1"></i> Baixar contrato
              </button>
              <button type="submit" class="btn btn-success btn-sm">
                <i class="bi bi-check me-1"></i> Salvar alterações
              </button>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeEditContractModal">Fechar</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Renovar Contrato -->
  <div class="modal fade" id="companyContractRenewModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Renovar contrato</h5>
          <button type="button" class="btn-close" @click="closeRenewContractModal"></button>
        </div>
        <div class="modal-body" v-if="contractToRenew">
          <div class="d-flex align-items-center bg-light-primary rounded p-3 mb-3" v-if="company">
            <div class="me-3">
              <i class="bi bi-building fs-2 text-primary"></i>
            </div>
            <div class="flex-grow-1">
              <span class="fw-bold text-dark">{{ company.company_name }}</span>
              <span class="text-muted ms-2">{{ company.formatted_cnpj }}</span>
            </div>
          </div>
          <p class="text-muted mb-2">
            Contrato <strong>{{ contractToRenew.contract_number || ('#' + contractToRenew.id_contract) }}</strong>
            — {{ contractToRenew.plan?.plan_name || '-' }}
          </p>
          <div class="alert alert-info mb-3" v-if="renewValidityPeriod">
            Nova vigência: <strong>{{ renewValidityPeriod.start }}</strong> a <strong>{{ renewValidityPeriod.end }}</strong>
          </div>
          <form @submit.prevent="submitRenewContract">
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label required">Valor por titular</label>
                <money3 class="form-control form-control-solid"
                       v-model="renewForm.subs_base_price"
                       v-bind="moneyConfig" />
              </div>
              <div class="col-md-6">
                <label class="form-label required">Valor por dependente</label>
                <money3 class="form-control form-control-solid"
                       v-model="renewForm.subs_dependent_price"
                       v-bind="moneyConfig" />
              </div>
            </div>
            <div class="modal-footer px-0 pb-0">
              <button type="button" class="btn btn-secondary" @click="closeRenewContractModal">Cancelar</button>
              <button type="submit" class="btn btn-primary">
                <i class="bi bi-check me-1"></i> Confirmar renovação
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Histórico de Renovações -->
  <div class="modal fade" id="companyContractRenewHistoryModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Histórico de renovações</h5>
          <button type="button" class="btn-close" @click="closeRenewHistoryModal"></button>
        </div>
        <div class="modal-body" v-if="contractRenewHistory">
          <p class="text-muted mb-3">
            Contrato <strong>{{ contractRenewHistory.contract_number || ('#' + contractRenewHistory.id_contract) }}</strong>
            — {{ contractRenewHistory.plan?.plan_name || '-' }}
          </p>
          <div class="d-flex justify-content-end align-items-center mb-3">
            <div class="btn-group btn-group-sm" role="group">
              <button type="button" class="btn"
                      :class="renewHistoryViewMode === 'cards' ? 'btn-primary' : 'btn-light'"
                      @click="renewHistoryViewMode = 'cards'" title="Visualizar em cards">
                <i class="bi bi-grid-3x3-gap"></i>
              </button>
              <button type="button" class="btn"
                      :class="renewHistoryViewMode === 'table' ? 'btn-primary' : 'btn-light'"
                      @click="renewHistoryViewMode = 'table'" title="Visualizar em tabela">
                <i class="bi bi-list-ul"></i>
              </button>
            </div>
          </div>
          <div v-if="renewsLoading" class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-1"></span> Carregando...
          </div>
          <template v-else-if="renewsList.length === 0">
            <div class="text-center text-muted py-4">Nenhuma renovação registrada.</div>
          </template>
          <div v-else-if="renewHistoryViewMode === 'cards'" class="row g-3">
            <div v-for="r in renewsList" :key="r.id_contract_renew" class="col-12 col-md-6 col-lg-6">
              <div class="card card-bordered h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <span class="badge bg-primary text-muted small">{{ formatDateTime(r.renewed_at || r.created_at) }}</span>
                  </div>
                  <div class="mb-2 fs-7">
                    <strong>Período:</strong> {{ formatDate(r.start_date) }} — {{ formatDate(r.valid_until) }}
                  </div>
                  <div class="d-flex flex-wrap gap-3 mb-2 fs-7">
                    <span><strong>Titular:</strong> {{ formatMoney(r.subs_base_price) }}</span>
                    <span><strong>Dependente:</strong> {{ formatMoney(r.subs_dependent_price) }}</span>
                  </div>
                  <div class="fs-7 text-muted">
                    <strong>Renovado por:</strong> {{ r.renewed_by?.user_name || '-' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover table-row-bordered">
              <thead>
                <tr class="fw-bold fs-6 text-muted">
                  <th>Data/hora renovação</th>
                  <th>Período</th>
                  <th>Valor titular</th>
                  <th>Valor dependente</th>
                  <th>Renovado por</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in renewsList" :key="r.id_contract_renew">
                  <td>{{ formatDateTime(r.renewed_at || r.created_at) }}</td>
                  <td>{{ formatDate(r.start_date) }} — {{ formatDate(r.valid_until) }}</td>
                  <td>{{ formatMoney(r.subs_base_price) }}</td>
                  <td>{{ formatMoney(r.subs_dependent_price) }}</td>
                  <td>{{ r.renewed_by?.user_name || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Confirmar Cancelamento de Contrato -->
  <div class="modal fade" id="companyContractCancelModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Cancelar contrato</h5>
          <button type="button" class="btn-close" @click="closeCancelContractModal"></button>
        </div>
        <div class="modal-body" v-if="contractToCancel">
          <p class="mb-3">
            Contrato <strong>{{ contractToCancel.contract_number || ('#' + contractToCancel.id_contract) }}</strong>
            — {{ contractToCancel.plan?.plan_name || '-' }}. Informe o motivo do cancelamento.
          </p>
          <div class="mb-3">
            <label class="form-label required">Motivo do cancelamento (cancellation_reason)</label>
            <textarea class="form-control form-control-solid"
                      v-model="cancelReason"
                      rows="3"
                      placeholder="Ex.: Rescisão por acordo entre as partes"
                      required></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeCancelContractModal">Voltar</button>
          <button type="button" class="btn btn-danger" :disabled="!cancelReason || !cancelReason.trim()"
                  @click="submitCancelContract">
            <i class="bi bi-x-circle me-1"></i> Confirmar cancelamento
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import Download from "@/helpers/download";
import { CompanyService } from "@/service/companyService";
import moment from "moment";
import Calendar from "primevue/calendar";
import { Money3 } from "v-money3";
import Contract from "../../contract/Contract.vue";

export default {
  name: "CompanyContractsModal",
  components: {Contract, Calendar, Money3 },
  props: ['company'],
  emits: ['onClose', 'contractGenerated'],
  data() {
    return {
      contracts: [],
      viewMode: 'card',
      companyService: CompanyService.new(),
      contractToEdit: null,
      newContractForm: { id_plan: null, valid_until: null, start_date: null, subs_base_price: null, subs_dependent_price: null },
      editContractForm: { start_date: null, valid_until: null, subs_base_price: null, subs_dependent_price: null },
      corporatePlans: [],
      contractToCancel: null,
      cancelReason: '',
      contractToRenew: null,
      renewForm: { subs_base_price: null, subs_dependent_price: null },
      contractRenewHistory: null,
      renewsList: [],
      renewsLoading: false,
      renewHistoryViewMode: 'cards',
      moneyConfig: {
        masked: false,
        prefix: 'R$ ',
        suffix: '',
        thousands: '.',
        decimal: ',',
        precision: 2,
        disableNegative: true,
        min: null,
        max: null,
        allowBlank: true,
        minimumNumberOfCharacters: 0,
        shouldRound: false,
        focusOnRight: false,
      },
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    currentUserId() {
      return this.$store.state.auth?.user?.id_user ?? null;
    },
    minValidUntilDate() {
      return new Date();
    },
    renewValidityPeriod() {
      const c = this.contractToRenew;
      if (!c || !c.valid_until || !c.start_date) return null;
      const validUntil = moment(c.valid_until);
      const startDate = moment(c.start_date);
      const periodDays = validUntil.diff(startDate, 'days') + 1;
      const novaDataInicio = validUntil.clone().add(1, 'day');
      const novaDataFim = novaDataInicio.clone().add(periodDays - 1, 'days');
      return {
        start: novaDataInicio.format('DD/MM/YYYY'),
        end: novaDataFim.format('DD/MM/YYYY'),
      };
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadContracts();
        } else {
          this.contracts = [];
        }
      }
    },
    'newContractForm.start_date'(startDate) {
      if (startDate) {
        const end = moment(startDate).add(12, 'months').subtract(1, 'day').toDate();
        this.newContractForm.valid_until = end;
      }
    },
    'editContractForm.start_date'(startDate) {
      if (startDate) {
        const end = moment(startDate).add(12, 'months').subtract(1, 'day').toDate();
        this.editContractForm.valid_until = end;
      }
    },
    'newContractForm.id_plan'() {
      this.applyPlanPricesToNewContract();
    },
    corporatePlans: {
      handler() {
        this.applyPlanPricesToNewContract();
      },
      deep: true
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },
    formatDateTime(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    formatMoney(value) {
      if (value == null || value === '') return '-';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
    },
    getContractStatusType(contract) {
      if (!contract || contract.contract_status !== 'active') return contract?.contract_status || null;
      const validUntil = contract.valid_until ? moment(contract.valid_until).startOf('day') : null;
      if (!validUntil || !validUntil.isValid()) return 'active';
      const today = moment().startOf('day');
      const in30Days = moment().add(30, 'days').startOf('day');
      if (validUntil.isBefore(today) || contract.is_expired === true) return 'expired';
      if (validUntil.isSameOrBefore(in30Days)) return 'expiring';
      return 'active';
    },
    getStatusDisplayText(contract) {
      const statusType = this.getContractStatusType(contract);
      const labels = {
        active: 'Ativo',
        expiring: 'A vencer',
        expired: 'Vencido',
        cancelled: 'Cancelado',
        expired_status: 'Expirado',
        pending: 'Pendente',
      };
      if (statusType === 'expiring') return labels.expiring;
      if (statusType === 'expired') return labels.expired;
      if (statusType === 'active') return labels.active;
      return labels[contract.contract_status] || contract.contract_status_dsc || '-';
    },
    getStatusBadgeClass(contract) {
      const statusType = this.getContractStatusType(contract);
      const classes = {
        active: 'badge badge-success',
        expiring: 'badge badge-info',
        expired: 'badge badge-warning',
        cancelled: 'badge badge-danger',
        pending: 'badge badge-info',
      };
      if (statusType) return classes[statusType] || classes[contract.contract_status] || 'badge badge-secondary';
      return classes[contract.contract_status] || 'badge badge-secondary';
    },
    loadContracts() {
      if (!this.company) return;

      this.companyService.contracts(this.place.id_place, this.company.id_company, {
        per_page: 100
      }).then((response) => {
        const paginatedData = response.data;
        this.contracts = paginatedData?.data || [];
      }).catch((error) => {
        console.error('Erro ao carregar contratos:', error);
        Alert.error('Erro ao carregar contratos.');
        this.contracts = [];
      });
    },
    downloadContract(contractId) {
      Loader.show('Baixando contrato...');
      http.get(`/places/${this.place.id_place}/contracts/${contractId}/download`, {
        responseType: 'blob'
      }).then((response) => {
        const contract = this.contracts.find(c => c.id_contract === contractId);
        const filename = `contrato-${this.company.company_name}-${contract?.contract_number || contractId}.pdf`;
        Download.downloadFile(response.data, filename, 'application/pdf');
        Loader.close();
      }).catch((error) => {
        Loader.close();
        const errorMessage = error.response?.data?.message || 
                            (typeof error.response?.data === 'string' ? error.response.data : null) ||
                            error.response?.data ||
                            'Erro ao baixar contrato.';
        Alert.error(errorMessage);
      });
    },
    closeModal() {
      Modal.toggle('#companyContractsModal');
      this.closeEditContractModal();
      this.closeNewContractModal();
      this.closeCancelContractModal();
      this.closeRenewContractModal();
      this.closeRenewHistoryModal();
      this.$emit('onClose');
    },
    openCancelContractModal(contract) {
      if (!contract || contract.contract_status !== 'active') return;
      this.contractToCancel = { ...contract };
      this.cancelReason = '';
      this.$nextTick(() => Modal.toggle('#companyContractCancelModal'));
    },
    closeCancelContractModal() {
      Modal.hide('#companyContractCancelModal');
      this.contractToCancel = null;
      this.cancelReason = '';
    },
    submitCancelContract() {
      const reason = (this.cancelReason || '').trim();
      if (!reason || !this.contractToCancel) return;
      Loader.show('Cancelando contrato...');
      const payload = {
        cancellation_reason: reason,
        id_user_cancelled: this.currentUserId,
      };
      this.companyService.cancelContract(this.place.id_place, this.company.id_company, this.contractToCancel.id_contract, payload)
        .then(() => {
          Loader.close();
          Alert.success('Contrato cancelado com sucesso.');
          this.closeCancelContractModal();
          this.loadContracts();
          this.$emit('contractGenerated');
        })
        .catch((error) => {
          Loader.close();
          const msg = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Erro ao cancelar contrato.';
          Alert.error(msg);
        });
    },
    canRenewContract(contract) {
      if (!contract || contract.contract_status !== 'active') return false;
      if (contract.is_expired === true) return true;
      const validUntil = contract.valid_until ? moment(contract.valid_until).startOf('day') : null;
      if (!validUntil) return false;
      const today = moment().startOf('day');
      const in30Days = moment().add(30, 'days').startOf('day');
      return validUntil.isSameOrBefore(in30Days);
    },
    openRenewContractModal(contract) {
      if (!contract || !this.canRenewContract(contract)) return;
      this.contractToRenew = { ...contract };
      this.renewForm = { subs_base_price: null, subs_dependent_price: null };
      this.loadPlanPricesForRenew(contract);
      this.$nextTick(() => Modal.toggle('#companyContractRenewModal'));
    },
    closeRenewContractModal() {
      Modal.hide('#companyContractRenewModal');
      this.contractToRenew = null;
      this.renewForm = { subs_base_price: null, subs_dependent_price: null };
    },
    loadPlanPricesForRenew(contract) {
      const plan = contract.plan;
      const fallbackBase = contract.subs_base_price != null ? Number(contract.subs_base_price) : null;
      const fallbackDep = contract.subs_dependent_price != null ? Number(contract.subs_dependent_price) : null;
      if (plan && (plan.plan_base_price != null || plan.plan_dependent_price != null)) {
        this.renewForm.subs_base_price = plan.plan_base_price != null ? Number(plan.plan_base_price) : fallbackBase;
        this.renewForm.subs_dependent_price = plan.plan_dependent_price != null ? Number(plan.plan_dependent_price) : fallbackDep;
        return;
      }
      http.get(`/places/${this.place.id_place}/plans`, {
        params: { plan_company: true, per_page: 1000 }
      }).then((response) => {
        const raw = response.data?.data ?? response.data;
        const plans = Array.isArray(raw) ? raw : [];
        const p = plans.find(x => x.id_plan === contract.id_plan);
        if (p) {
          this.renewForm.subs_base_price = p.plan_base_price != null ? Number(p.plan_base_price) : fallbackBase;
          this.renewForm.subs_dependent_price = p.plan_dependent_price != null ? Number(p.plan_dependent_price) : fallbackDep;
        } else {
          this.renewForm.subs_base_price = fallbackBase;
          this.renewForm.subs_dependent_price = fallbackDep;
        }
      }).catch(() => {
        this.renewForm.subs_base_price = fallbackBase;
        this.renewForm.subs_dependent_price = fallbackDep;
      });
    },
    submitRenewContract() {
      if (!this.contractToRenew) return;
      const basePrice = this.renewForm.subs_base_price != null && this.renewForm.subs_base_price !== ''
        ? Number(this.renewForm.subs_base_price) : null;
      const depPrice = this.renewForm.subs_dependent_price != null && this.renewForm.subs_dependent_price !== ''
        ? Number(this.renewForm.subs_dependent_price) : null;
      Loader.show('Renovando contrato...');
      const payload = { subs_base_price: basePrice, subs_dependent_price: depPrice };
      this.companyService.renewContract(this.place.id_place, this.company.id_company, this.contractToRenew.id_contract, payload)
        .then(() => {
          Loader.close();
          Alert.success('Contrato renovado com sucesso.');
          this.closeRenewContractModal();
          this.loadContracts();
          this.$emit('contractGenerated');
        })
        .catch((error) => {
          Loader.close();
          const msg = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Erro ao renovar contrato.';
          Alert.error(msg);
        });
    },
    openRenewHistoryModal(contract) {
      if (!contract) return;
      this.contractRenewHistory = { ...contract };
      this.renewsList = [];
      this.renewsLoading = true;
      this.$nextTick(() => Modal.toggle('#companyContractRenewHistoryModal'));
      this.loadContractRenews();
    },
    closeRenewHistoryModal() {
      Modal.hide('#companyContractRenewHistoryModal');
      this.contractRenewHistory = null;
      this.renewsList = [];
      this.renewHistoryViewMode = 'cards';
    },
    loadContractRenews() {
      if (!this.contractRenewHistory) return;
      this.companyService.contractRenews(this.place.id_place, this.company.id_company, this.contractRenewHistory.id_contract)
        .then((response) => {
          const data = response.data?.data ?? response.data;
          this.renewsList = Array.isArray(data) ? [...data].sort((a, b) => (a.renew_seq ?? 0) - (b.renew_seq ?? 0)) : [];
        })
        .catch(() => {
          this.renewsList = [];
          Alert.error('Erro ao carregar histórico de renovações.');
        })
        .finally(() => {
          this.renewsLoading = false;
        });
    },
    confirmReopenContract(contract) {
      if (!contract || contract.contract_status !== 'cancelled') return;
      Swal.fire({
        title: 'Reabrir contrato',
        html: `Deseja reabrir o contrato <strong>${contract.contract_number || ('#' + contract.id_contract)}</strong>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reabrir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#50cd89',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Reabrindo contrato...');
          this.companyService.reopenContract(this.place.id_place, this.company.id_company, contract.id_contract)
            .then(() => {
              Loader.close();
              Alert.success('Contrato reaberto com sucesso.');
              this.loadContracts();
              this.$emit('contractGenerated');
            })
            .catch((error) => {
              Loader.close();
              const msg = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Erro ao reabrir contrato.';
              Alert.error(msg);
            });
        }
      });
    },
    openEditContractModal(contract) {
      if (!contract) return;
      this.contractToEdit = { ...contract };
      this.editContractForm = {
        start_date: contract.start_date ? (typeof contract.start_date === 'string' ? new Date(contract.start_date) : contract.start_date) : null,
        valid_until: contract.valid_until ? (typeof contract.valid_until === 'string' ? new Date(contract.valid_until) : contract.valid_until) : null,
        subs_base_price: contract.subs_base_price ?? null,
        subs_dependent_price: contract.subs_dependent_price ?? null,
      };
      this.$nextTick(() => Modal.toggle('#companyContractEditModal'));
    },
    closeEditContractModal() {
      Modal.hide('#companyContractEditModal');
      this.contractToEdit = null;
      this.editContractForm = { start_date: null, valid_until: null, subs_base_price: null, subs_dependent_price: null };
    },
    openNewContractModal() {
      if (!this.company) return;
      this.newContractForm = {
        id_plan: this.company.id_plan || null,
        valid_until: null,
        start_date: null,
        subs_base_price: null,
        subs_dependent_price: null,
      };
      this.loadCorporatePlans();
      this.$nextTick(() => Modal.toggle('#companyContractNewModal'));
    },
    closeNewContractModal() {
      Modal.hide('#companyContractNewModal');
      this.newContractForm = { id_plan: null, valid_until: null, start_date: null, subs_base_price: null, subs_dependent_price: null };
    },
    loadCorporatePlans() {
      http.get(`/places/${this.place.id_place}/plans`, {
        params: { plan_company: true, per_page: 1000 }
      }).then((response) => {
        const raw = response.data?.data ?? response.data;
        this.corporatePlans = Array.isArray(raw) ? raw : [];
      }).catch(() => {
        this.corporatePlans = [];
      });
    },
    applyPlanPricesToNewContract() {
      if (!this.newContractForm.id_plan || !this.corporatePlans.length) return;
      const plan = this.corporatePlans.find(p => p.id_plan === this.newContractForm.id_plan);
      if (plan) {
        this.newContractForm.subs_base_price = plan.plan_base_price != null ? Number(plan.plan_base_price) : null;
        this.newContractForm.subs_dependent_price = plan.plan_dependent_price != null ? Number(plan.plan_dependent_price) : null;
      }
    },
    submitNewContract() {
      if (!this.newContractForm.id_plan) {
        Alert.error('Selecione o plano.');
        return;
      }
      if (!this.newContractForm.valid_until) {
        Alert.error('Informe a vigência (válido até).');
        return;
      }
      if (!this.newContractForm.start_date) {
        Alert.error('Informe a data início.');
        return;
      }
      if (this.newContractForm.subs_base_price == null || this.newContractForm.subs_base_price === '') {
        Alert.error('Informe o valor titular (subs_base_price).');
        return;
      }
      if (this.newContractForm.subs_dependent_price == null || this.newContractForm.subs_dependent_price === '') {
        Alert.error('Informe o valor dependente (subs_dependent_price).');
        return;
      }
      Loader.show('Gerando novo contrato...');
      const payload = {
        id_plan: this.newContractForm.id_plan,
        valid_until: this.newContractForm.valid_until instanceof Date
          ? moment(this.newContractForm.valid_until).format('YYYY-MM-DD')
          : this.newContractForm.valid_until,
        start_date: this.newContractForm.start_date instanceof Date
          ? moment(this.newContractForm.start_date).format('YYYY-MM-DD')
          : this.newContractForm.start_date,
        subs_base_price: Number(this.newContractForm.subs_base_price),
        subs_dependent_price: Number(this.newContractForm.subs_dependent_price),
      };
      this.companyService.generateContract(this.place.id_place, this.company.id_company, payload)
        .then(() => {
          Loader.close();
          Alert.success('Contrato gerado com sucesso.');
          this.closeNewContractModal();
          this.loadContracts();
          this.$emit('contractGenerated');
        })
        .catch((error) => {
          Loader.close();
          const msg = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Erro ao gerar contrato.';
          Alert.error(msg);
        });
    },
    submitEditContract() {
      if (!this.contractToEdit) return;
      Loader.show('Salvando contrato...');
      const payload = {
        start_date: this.editContractForm.start_date instanceof Date
          ? moment(this.editContractForm.start_date).format('YYYY-MM-DD')
          : (this.editContractForm.start_date || null),
        valid_until: this.editContractForm.valid_until instanceof Date
          ? moment(this.editContractForm.valid_until).format('YYYY-MM-DD')
          : (this.editContractForm.valid_until || null),
        subs_base_price: this.editContractForm.subs_base_price != null && this.editContractForm.subs_base_price !== ''
          ? Number(this.editContractForm.subs_base_price)
          : null,
        subs_dependent_price: this.editContractForm.subs_dependent_price != null && this.editContractForm.subs_dependent_price !== ''
          ? Number(this.editContractForm.subs_dependent_price)
          : null,
      };
      this.companyService.updateContract(this.place.id_place, this.company.id_company, this.contractToEdit.id_contract, payload)
        .then(() => {
          Loader.close();
          Alert.success('Contrato atualizado com sucesso.');
          this.closeEditContractModal();
          this.loadContracts();
          this.$emit('contractGenerated');
        })
        .catch((error) => {
          Loader.close();
          const msg = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Erro ao atualizar contrato.';
          Alert.error(msg);
        });
    },
  }
}
</script>
